import {FC, useRef, useCallback, useEffect} from 'react'
import {useTranslation} from '@/translations/i18n'
import {DetailsSidebar, DetailsSidebarTabs} from '@/components/ui/details-atoms/DetailsAtoms'
import {EmptyState} from '@/components/commons/empty-state/EmptyState'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Spinner} from '@/components/ui/spinner/Spinner'
import {ListIcon} from '@/components/ui/icon/Icon'
import {Button} from '@/components/ui/button/Button'
import {ScrollArea} from '@/components/ui/scroll-area/ScrollArea'
import {Scrollbar} from '@/components/ui/scrollbar/Scrollbar'
import {ActivityLogItem} from '@/features/entity-activity-logs/components/activity-log-item/ActivityLogItem'
import {EntityUrlType, ActivityLogsQueryParams} from '@/features/entity-activity-logs/types'
import {useGetEntityActivityLogs} from '@/features/entity-activity-logs/queries/useGetEntityActivityLogs'
import {StyledEntityActivityLogs} from './style'

interface EntityActivityLogsProps {
    entityType: EntityUrlType
    entityId?: string
    queryParams?: Omit<ActivityLogsQueryParams, 'page'>
}

export const EntityActivityLogs: FC<EntityActivityLogsProps> = ({entityType, entityId, queryParams}) => {
    const {t} = useTranslation()
    const endOfListRef = useRef<HTMLDivElement>(null)

    const {
        remappedData: logs,
        isLoading,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        isError
    } = useGetEntityActivityLogs({
        entityType,
        entityId,
        queryParams: {
            limit: 10,
            responseType: 'extended',
            ...queryParams
        },
        options: {enabled: !!entityId}
    })

    const handleObserver = useCallback(
        (entries: IntersectionObserverEntry[]) => {
            const [entry] = entries
            if (entry.isIntersecting && hasNextPage && !isLoading && !isFetchingNextPage) {
                fetchNextPage()
            }
        },
        [hasNextPage, isLoading, isFetchingNextPage, fetchNextPage]
    )

    useEffect(() => {
        if (!hasNextPage || !endOfListRef.current) return

        const observer = new IntersectionObserver(handleObserver, {
            root: null,
            rootMargin: '0px',
            threshold: 0.5
        })

        const currentRef = endOfListRef.current
        observer.observe(currentRef)

        return () => observer.unobserve(currentRef)
    }, [handleObserver, hasNextPage, logs.length])

    return (
        <DetailsSidebar>
            <DetailsSidebarTabs>
                <Button variant="ghost" size="sm" shape="roundedRectangle">
                    <ListIcon />
                    {t('commons:activities')}
                </Button>
            </DetailsSidebarTabs>

            <StyledEntityActivityLogs>
                {isLoading && logs.length === 0 && (
                    <Flexbox align="center" justify="center" fullWidth style={{padding: '20px 0'}}>
                        <Spinner size={24} />
                    </Flexbox>
                )}

                {isError && (
                    <EmptyState
                        title={t('commons:error_loading_data')}
                        subtitle={t('commons:please_try_again')}
                        variant="secondary"
                    />
                )}

                {!isLoading && !isError && logs.length === 0 && (
                    <EmptyState
                        title={t('commons:no_logs_available')}
                        subtitle={t('commons:no_activity_recorded')}
                        variant="secondary"
                    />
                )}

                {logs.length > 0 && (
                    <ScrollArea scrollbar={<Scrollbar />} style={{height: '100%'}}>
                        <Flexbox direction="column" gap={3} fullWidth>
                            {logs.map((log, index) => (
                                <ActivityLogItem key={log.id} log={log} showDivider={index < logs.length - 1} />
                            ))}

                            {(hasNextPage || isFetchingNextPage) && (
                                <div ref={endOfListRef} style={{height: '20px', padding: '10px 0'}}>
                                    {isFetchingNextPage && (
                                        <Flexbox justify="center" align="center">
                                            <Spinner size={20} />
                                        </Flexbox>
                                    )}
                                </div>
                            )}
                        </Flexbox>
                    </ScrollArea>
                )}
            </StyledEntityActivityLogs>
        </DetailsSidebar>
    )
}
